import { Link } from 'react-router-dom'
import { Instagram, Facebook, Youtube, Mail, Phone, MapPin, Clock } from 'lucide-react'
import { NAV_LINKS, CONTACT_INFO, SOCIAL_LINKS, OPENING_HOURS } from '../../lib/constants'

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
)

function Footer() {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {/* Column 1: Social Links */}
          <div className="lg:col-span-1">
            <h3 className="font-abandoned text-lg mb-4 text-white">Suivez-moi</h3>
            {/* Social Links */}
            <div className="flex gap-3">
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 p-2.5 rounded-full hover:bg-primary transition-colors duration-200"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 p-2.5 rounded-full hover:bg-primary transition-colors duration-200"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href={SOCIAL_LINKS.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 p-2.5 rounded-full hover:bg-primary transition-colors duration-200"
                aria-label="YouTube"
              >
                <Youtube className="h-5 w-5" />
              </a>
              <a
                href={SOCIAL_LINKS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 p-2.5 rounded-full hover:bg-[#25D366] transition-colors duration-200"
                aria-label="WhatsApp"
              >
                <WhatsAppIcon className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Column 2: Navigation */}
          <div className="hidden md:block">
            <h3 className="font-abandoned text-lg mb-4 text-white">Navigation</h3>
            <ul className="space-y-3">
              {NAV_LINKS.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Contact */}
          <div>
            <h3 className="font-abandoned text-lg mb-4 text-white">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(CONTACT_INFO.address.full)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {CONTACT_INFO.address.street}
                    <br />
                    {CONTACT_INFO.address.postalCode} {CONTACT_INFO.address.city}
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-primary shrink-0" />
                <a
                  href={CONTACT_INFO.phoneLink}
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  {CONTACT_INFO.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-primary shrink-0" />
                <a
                  href={CONTACT_INFO.emailLink}
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  {CONTACT_INFO.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Opening Hours */}
          <div>
            <h3 className="font-abandoned text-lg lg:text-xl mb-4 text-white flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Horaires
            </h3>
            <ul className="space-y-2 text-sm lg:text-base">
              {OPENING_HOURS.filter(h => h.morning !== 'Fermé' || h.afternoon !== 'Fermé').map((hours) => (
                <li key={hours.day} className="flex justify-between text-gray-400">
                  <span className="font-medium text-gray-300">{hours.day}</span>
                  <span>
                    {hours.morning !== 'Fermé' && hours.afternoon !== 'Fermé'
                      ? `${hours.afternoon.replace('Ouvert (', '').replace(')', '')}`
                      : hours.morning !== 'Fermé'
                        ? hours.morning.replace('Ouvert (', '').replace(')', '')
                        : hours.afternoon.replace('Ouvert (', '').replace(')', '')}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs lg:text-sm text-gray-500 mt-3">
              Matins sur rendez-vous uniquement
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container-custom py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm text-center md:text-left">
            &copy; {currentYear} {CONTACT_INFO.businessName} - Tous droits réservés
          </p>
          <div className="flex gap-6 text-sm">
            <Link
              to="/mentions-legales"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Mentions légales
            </Link>
            <Link
              to="/politique-confidentialite"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Politique de confidentialité
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
